/**
 * Account Auth Context
 * Manages authentication state for account pages (WebView)
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const AccountAuthContext = createContext(null);

// Mock user data - TODO: Replace with API call
const MOCK_USER_DATA = {
  user: {
    id: 'user-123',
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    memberSince: '2024-01-15'
  },
  subscription: {
    planId: 'pro',
    planName: 'Pro Plan',
    status: 'active',
    billingCycle: 'monthly',
    currentPeriodStart: '2025-01-15',
    currentPeriodEnd: '2025-02-15',
    nextPaymentDate: '2025-02-15',
    nextPaymentAmount: 49.00,
    cancelAtPeriodEnd: false
  },
  defaultPaymentMethod: {
    id: 'pm-123',
    brand: 'visa',
    last4: '4242',
    expMonth: 12,
    expYear: 2026
  }
};

export const AccountAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [error, setError] = useState(null);

  // Check if user is already logged in (from session)
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = sessionStorage.getItem('account_token');
      if (token) {
        // TODO: Validate token with API
        // For now, use mock data
        setUser(MOCK_USER_DATA.user);
        setSubscription(MOCK_USER_DATA.subscription);
        setDefaultPaymentMethod(MOCK_USER_DATA.defaultPaymentMethod);
        setIsAuthenticated(true);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setError('Failed to verify authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the general API with type: sub to get subscription data along with login
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'sub',
          email: email,
          pwd: password
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        const d = result.data; // short keys
        
        // Store credentials for later API calls
        sessionStorage.setItem('account_token', 'authenticated');
        sessionStorage.setItem('account_email', email);
        sessionStorage.setItem('account_pwd', password);
        
        // Get user name from webview token validation if available
        const webviewUser = sessionStorage.getItem('webview_user');
        const userName = webviewUser ? JSON.parse(webviewUser).name : email.split('@')[0];
        
        // Map user from short keys
        const mappedUser = {
          id: d.bid,
          name: userName,
          email: email,
          businessName: d.bn || null,
          addressLine1: d.bal1 || null,
          addressLine2: d.bal2 || null,
          city: d.bcy || null,
          state: d.bst || null,
          zipcode: d.bzip || null,
          memberSince: d.bca || null,
        };
        
        // Map subscription from short keys
        const mappedSubscription = d.hs ? {
          planId: d.pid,
          planName: d.pn,
          status: d.st,
          billingCycle: 'monthly',
          currentPeriodStart: d.cps,
          currentPeriodEnd: d.cpe,
          nextPaymentDate: d.cpe,
          nextPaymentAmount: d.cmp || 0,
          cancelAtPeriodEnd: d.cap || false,
          inventoryCount: d.ic,
          locationCount: d.lc,
          maxInventoryUnits: d.miu || 0,
          maxLocations: d.ml || 0,
          trialInfo: {
            inTrial: d.it,
            daysRemaining: d.tdr
          },
          scheduledChange: d.sci ? {
            planId: d.sci.spid,
            planName: d.sci.spn,
            planPrice: d.sci.spp,
            scheduledAt: d.sci.sat,
            changeType: d.sci.sct
          } : null,
          features: d.ft ? {
            aiConversationsMonthly: d.ft.acm,
            aiMemoryEnabled: d.ft.ame,
            featuredListings: d.ft.fl,
            prioritySupport: d.ft.ps,
            voiceListingEnabled: d.ft.vle,
            lateFeeAutomation: d.ft.lfa,
            teamMembersLimit: d.ft.tml
          } : {}
        } : null;
        
        // Map payment method from short keys
        const mappedPaymentMethod = d.hpm ? {
          brand: d.cb,
          last4: d.cl4,
          expMonth: d.cem,
          expYear: d.cey
        } : null;
        
        // Set state
        setUser(mappedUser);
        setSubscription(mappedSubscription);
        setDefaultPaymentMethod(mappedPaymentMethod);
        setIsAuthenticated(true);
        
        return { success: true };
      } else {
        // Login failed
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      return { success: false, error: 'An error occurred. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // TODO: Call logout API
      // POST /account/logout
      sessionStorage.removeItem('account_token');
      sessionStorage.removeItem('webview_token');
      sessionStorage.removeItem('webview_user');
      setUser(null);
      setSubscription(null);
      setDefaultPaymentMethod(null);
      setIsAuthenticated(false);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const refreshUserData = async () => {
    try {
      const email = sessionStorage.getItem('account_email');
      const pwd = sessionStorage.getItem('account_pwd');
      if (!email || !pwd) return;
      
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'sub', email, pwd })
      });
      
      const result = await response.json();
      if (result.success) {
        const d = result.data;
        
        const mappedSubscription = d.hs ? {
          planId: d.pid,
          planName: d.pn,
          status: d.st,
          billingCycle: 'monthly',
          currentPeriodStart: d.cps,
          currentPeriodEnd: d.cpe,
          nextPaymentDate: d.cpe,
          nextPaymentAmount: d.cmp || 0,
          cancelAtPeriodEnd: d.cap || false,
          inventoryCount: d.ic,
          locationCount: d.lc,
          maxInventoryUnits: d.miu || 0,
          maxLocations: d.ml || 0,
          trialInfo: {
            inTrial: d.it,
            daysRemaining: d.tdr
          },
          scheduledChange: d.sci ? {
            planId: d.sci.spid,
            planName: d.sci.spn,
            planPrice: d.sci.spp,
            scheduledAt: d.sci.sat,
            changeType: d.sci.sct
          } : null,
          features: d.ft ? {
            aiConversationsMonthly: d.ft.acm,
            aiMemoryEnabled: d.ft.ame,
            featuredListings: d.ft.fl,
            prioritySupport: d.ft.ps,
            voiceListingEnabled: d.ft.vle,
            lateFeeAutomation: d.ft.lfa,
            teamMembersLimit: d.ft.tml
          } : {}
        } : null;
        
        const mappedPaymentMethod = d.hpm ? {
          brand: d.cb,
          last4: d.cl4,
          expMonth: d.cem,
          expYear: d.cey
        } : null;
        
        setSubscription(mappedSubscription);
        setDefaultPaymentMethod(mappedPaymentMethod);
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    subscription,
    defaultPaymentMethod,
    error,
    login,
    logout,
    refreshUserData
  };

  return (
    <AccountAuthContext.Provider value={value}>
      {children}
    </AccountAuthContext.Provider>
  );
};

export const useAccountAuth = () => {
  const context = useContext(AccountAuthContext);
  if (!context) {
    throw new Error('useAccountAuth must be used within AccountAuthProvider');
  }
  return context;
};

export default AccountAuthContext;
